import { motion } from "framer-motion";
import { Instagram, ExternalLink, ScrollText } from "lucide-react";
import { Link } from "react-router-dom";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";

const slugify = (s = "") =>
  s.toString().toLowerCase().trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const Creators = () => {
  const { content } = useContent();
  const creators = content?.creators || [];

  if (!creators.length) return null;

  return (
    <section
      id="creator"
      data-testid="creators-section"
      className="relative bg-[#050505] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Creator" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              04 — Creator / Talent
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl max-w-3xl">
              Gesichter der <br />
              <span className="text-glow-red text-white">Straße.</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            Content Creator im Management von Straßenwert. Für Kooperationen, Bookings & Anfragen — direkter Kontakt zum Management.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {creators.map((c, i) => {
            const slug = c.slug || slugify(c.name);
            return (
              <motion.div
                key={c.name + i}
                data-testid={`creator-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: i * 0.08, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                className="group relative aspect-[3/4] overflow-hidden border border-white/[0.06] bg-[#0A0A0A]"
              >
                <Link
                  to={`/creator/${slug}`}
                  className="absolute inset-0 z-10"
                  data-testid={`creator-link-${i}`}
                  aria-label={c.name}
                />
                {c.image ? (
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000 pointer-events-none"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0A0A0A] via-[#050505] to-[#0A0A0A] pointer-events-none">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-700 font-bold">Kein Foto</div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-[#ff0033]/15 via-transparent to-transparent" />

                {c.role && (
                  <div
                    data-testid={`creator-role-${i}`}
                    className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 px-2.5 py-1 border border-[#ff0033]/50 bg-[#ff0033]/15 text-[9px] tracking-[0.3em] uppercase text-white font-bold backdrop-blur-md shadow-[0_0_18px_rgba(255,0,51,0.35)]"
                  >
                    <span className="red-led" />
                    {c.role}
                  </div>
                )}

                {c.instagram_url && (
                  <a
                    href={c.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`creator-instagram-${i}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-5 right-5 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 hover:border-[#ff0033]/60 px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-white hover:shadow-[0_0_16px_rgba(255,0,51,0.35)] transition-all"
                  >
                    <Instagram size={12} />
                    {c.instagram_handle || "Instagram"}
                  </a>
                )}

                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 z-10">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2">
                    {String(i + 1).padStart(2, "0")} / Creator
                  </div>
                  <div className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
                    {c.name}
                  </div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-2">
                    {c.tag}
                  </div>

                  <Link
                    to={`/creator/${slug}/impressum`}
                    data-testid={`creator-impressum-${i}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-5 inline-flex items-center gap-3 border-t border-white/15 pt-4 text-[11px] tracking-[0.25em] uppercase font-bold text-white hover:text-[#ff0033] transition-colors group/btn"
                  >
                    <ScrollText size={14} className="text-[#ff0033]" />
                    <span>Impressum</span>
                    <ExternalLink
                      size={12}
                      className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform"
                    />
                  </Link>
                </div>

                <div className="absolute top-0 left-0 right-0 h-px bg-[#ff0033] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 shadow-[0_0_12px_#ff0033]" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Creators;
